import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  listApiKeys,
  createApiKey,
  revokeApiKey,
  rotateApiKey,
  listOutboundWebhooks,
  createOutboundWebhook,
  updateOutboundWebhook,
  rotateWebhookSecret,
  deleteOutboundWebhook,
  listWebhookDeliveries,
  retryWebhookDelivery,
  sendTestWebhook,
} from "@/lib/api-keys.functions";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { PillTabsList, PillTabsTrigger } from "@/components/admin/pill-tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Copy, Plus, RotateCcw, Trash2, Send, KeyRound, Webhook, AlertTriangle, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/api-keys")({
  component: ApiKeysPage,
});

function copy(text: string) {
  navigator.clipboard.writeText(text).then(() => toast.success("Copied"));
}

function statusBadge(status: string) {
  if (status === "active") return <Badge variant="secondary" className="gap-1"><CheckCircle2 className="h-3 w-3 text-emerald-600" />active</Badge>;
  if (status === "revoked") return <Badge variant="destructive" className="gap-1"><XCircle className="h-3 w-3" />revoked</Badge>;
  return <Badge variant="outline">{status}</Badge>;
}

function deliveryBadge(status: string) {
  if (status === "success") return <Badge variant="secondary">success</Badge>;
  if (status === "failed") return <Badge variant="destructive">failed</Badge>;
  if (status === "retrying") return <Badge className="gap-1"><AlertTriangle className="h-3 w-3" />retrying</Badge>;
  return <Badge>{status}</Badge>;
}

function ApiKeysPage() {
  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-semibold">API keys & webhooks</h1>
        <p className="text-sm text-muted-foreground">
          Issue API tokens, configure outbound webhooks, and inspect delivery attempts.
        </p>
      </div>

      <Tabs defaultValue="keys">
        <PillTabsList search>
          <PillTabsTrigger value="keys" className="gap-1"><KeyRound className="h-4 w-4" /> API keys</PillTabsTrigger>
          <PillTabsTrigger value="webhooks" className="gap-1"><Webhook className="h-4 w-4" /> Outbound webhooks</PillTabsTrigger>
          <PillTabsTrigger value="deliveries">Deliveries</PillTabsTrigger>
          <PillTabsTrigger value="docs">Signing docs</PillTabsTrigger>
        </PillTabsList>

        <TabsContent value="keys" className="mt-4">
          <ApiKeysTab />
        </TabsContent>
        <TabsContent value="webhooks" className="mt-4">
          <WebhooksTab />
        </TabsContent>
        <TabsContent value="deliveries" className="mt-4">
          <DeliveriesTab />
        </TabsContent>
        <TabsContent value="docs" className="mt-4">
          <SigningDocs />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* -------------------- API KEYS TAB -------------------- */
function ApiKeysTab() {
  const fetchKeys = useServerFn(listApiKeys);
  const create = useServerFn(createApiKey);
  const revoke = useServerFn(revokeApiKey);
  const rotate = useServerFn(rotateApiKey);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({ queryKey: ["api_keys"], queryFn: () => fetchKeys() });

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({
    name: "",
    kind: "personal" as "personal" | "service",
    scopes: "",
    rate_limit_per_minute: 60,
    expires_at: "",
  });
  const [revealed, setRevealed] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: () =>
      create({
        data: {
          name: draft.name,
          kind: draft.kind,
          scopes: draft.scopes.split(",").map((s) => s.trim()).filter(Boolean),
          rate_limit_per_minute: draft.rate_limit_per_minute,
          expires_at: draft.expires_at ? new Date(draft.expires_at).toISOString() : null,
        },
      }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["api_keys"] });
      setRevealed(r.plaintext);
      setOpen(false);
      setDraft({ name: "", kind: "personal", scopes: "", rate_limit_per_minute: 60, expires_at: "" });
    },
    onError: (e: any) => toast.error(e?.message ?? "Create failed"),
  });

  const revokeMut = useMutation({
    mutationFn: (id: string) => revoke({ data: { id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["api_keys"] });
      toast.success("Key revoked");
    },
    onError: (e: any) => toast.error(e?.message ?? "Revoke failed"),
  });

  const rotateMut = useMutation({
    mutationFn: (id: string) => rotate({ data: { id } }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["api_keys"] });
      setRevealed(r.plaintext);
    },
    onError: (e: any) => toast.error(e?.message ?? "Rotate failed"),
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="text-base">API tokens</CardTitle>
            <CardDescription>Personal and service tokens with scopes, expiry, and rate limits.</CardDescription>
          </div>
          <Button onClick={() => setOpen(true)}><Plus className="h-4 w-4 mr-1" /> New token</Button>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Kind</TableHead>
              <TableHead>Prefix</TableHead>
              <TableHead>Scopes</TableHead>
              <TableHead>Rate / min</TableHead>
              <TableHead>Last used</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead>Status</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>
            )}
            {!isLoading && (data?.keys ?? []).length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">No API tokens yet.</TableCell></TableRow>
            )}
            {(data?.keys ?? []).map((k: any) => (
              <TableRow key={k.id}>
                <TableCell className="font-medium">{k.name}</TableCell>
                <TableCell><Badge variant="outline">{k.kind}</Badge></TableCell>
                <TableCell className="font-mono text-xs">{k.prefix}</TableCell>
                <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">{(k.scopes ?? []).join(", ") || "—"}</TableCell>
                <TableCell className="text-sm">{k.rate_limit_per_minute}</TableCell>
                <TableCell className="text-xs whitespace-nowrap">{k.last_used_at ? new Date(k.last_used_at).toLocaleString() : "—"}</TableCell>
                <TableCell className="text-xs whitespace-nowrap">{k.expires_at ? new Date(k.expires_at).toLocaleDateString() : "—"}</TableCell>
                <TableCell>{statusBadge(k.status)}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => rotateMut.mutate(k.id)} disabled={k.status === "revoked"}>
                    <RotateCcw className="h-3 w-3 mr-1" /> Rotate
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => revokeMut.mutate(k.id)} disabled={k.status === "revoked"}>
                    <Trash2 className="h-3 w-3 mr-1" /> Revoke
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create API token</DialogTitle>
            <DialogDescription>The plaintext token is shown once after creation.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Name</Label>
              <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="CI deploy bot" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Kind</Label>
                <Select value={draft.kind} onValueChange={(v: any) => setDraft({ ...draft, kind: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="personal">Personal</SelectItem>
                    <SelectItem value="service">Service</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Rate limit / min</Label>
                <Input type="number" value={draft.rate_limit_per_minute} onChange={(e) => setDraft({ ...draft, rate_limit_per_minute: Number(e.target.value) || 60 })} />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Scopes (comma-separated)</Label>
              <Input value={draft.scopes} onChange={(e) => setDraft({ ...draft, scopes: e.target.value })} placeholder="orders.read, invoices.write" />
            </div>
            <div className="space-y-1">
              <Label>Expires (optional)</Label>
              <Input type="datetime-local" value={draft.expires_at} onChange={(e) => setDraft({ ...draft, expires_at: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => createMut.mutate()} disabled={!draft.name || createMut.isPending}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revealed secret dialog */}
      <Dialog open={!!revealed} onOpenChange={(o) => !o && setRevealed(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Copy your token</DialogTitle>
            <DialogDescription>This is the only time the full token is shown. Store it securely.</DialogDescription>
          </DialogHeader>
          <div className="rounded-md border bg-muted p-3 font-mono text-xs break-all">{revealed}</div>
          <DialogFooter>
            <Button variant="outline" onClick={() => revealed && copy(revealed)}><Copy className="h-4 w-4 mr-1" /> Copy</Button>
            <Button onClick={() => setRevealed(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

/* -------------------- WEBHOOKS TAB -------------------- */

const EVENT_CATALOG: { group: string; events: string[] }[] = [
  { group: "Orders", events: ["order.created", "order.paid", "order.fulfilled", "order.cancelled", "order.refunded"] },
  { group: "Invoices", events: ["invoice.created", "invoice.paid", "invoice.overdue", "invoice.voided"] },
  { group: "Users", events: ["user.created", "user.updated", "user.deleted"] },
  { group: "Licenses", events: ["license.issued", "license.revoked"] },
  { group: "Support", events: ["ticket.created", "ticket.updated", "ticket.closed"] },
  { group: "Wallet", events: ["wallet.credited", "wallet.debited"] },
];

function EventPicker({
  selected,
  onChange,
}: {
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const [custom, setCustom] = useState("");
  const toggle = (ev: string) =>
    onChange(selected.includes(ev) ? selected.filter((e) => e !== ev) : [...selected, ev]);
  const addCustom = () => {
    const v = custom.trim();
    if (!v) return;
    if (!/^[a-zA-Z0-9_.\-]+$/.test(v)) return toast.error("Use letters, numbers, . _ -");
    if (selected.includes(v)) return setCustom("");
    if (selected.length >= 60) return toast.error("Max 60 events");
    onChange([...selected, v]);
    setCustom("");
  };
  const known = new Set(EVENT_CATALOG.flatMap((g) => g.events));
  const customSelected = selected.filter((e) => !known.has(e));

  return (
    <div className="space-y-3">
      {EVENT_CATALOG.map((g) => (
        <div key={g.group} className="space-y-1.5">
          <div className="text-xs font-medium text-muted-foreground">{g.group}</div>
          <div className="flex flex-wrap gap-1.5">
            {g.events.map((ev) => {
              const on = selected.includes(ev);
              return (
                <button
                  key={ev}
                  type="button"
                  onClick={() => toggle(ev)}
                  className={`rounded-md border px-2 py-1 text-xs transition-colors ${
                    on
                      ? "border-primary bg-primary/10 text-foreground"
                      : "border-border text-muted-foreground hover:bg-secondary"
                  }`}
                >
                  {on ? "✓ " : ""}{ev}
                </button>
              );
            })}
          </div>
        </div>
      ))}
      <div className="space-y-1.5">
        <div className="text-xs font-medium text-muted-foreground">Custom</div>
        {customSelected.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {customSelected.map((ev) => (
              <Badge key={ev} variant="secondary" className="gap-1">
                {ev}
                <button onClick={() => toggle(ev)} aria-label={`Remove ${ev}`}>×</button>
              </Badge>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          <Input
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            placeholder="e.g. project.deployed"
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); addCustom(); }
            }}
          />
          <Button type="button" variant="secondary" onClick={addCustom}>Add</Button>
        </div>
      </div>
      <div className="text-xs text-muted-foreground">
        {selected.length} event{selected.length === 1 ? "" : "s"} selected
      </div>
    </div>
  );
}

type Draft = {
  name: string;
  url: string;
  events: string[];
  description: string;
  retry_limit: number;
  timeout_ms: number;
};

const EMPTY_DRAFT: Draft = {
  name: "",
  url: "",
  events: [],
  description: "",
  retry_limit: 5,
  timeout_ms: 10000,
};

function WebhooksTab() {
  const fetchWh = useServerFn(listOutboundWebhooks);
  const create = useServerFn(createOutboundWebhook);
  const update = useServerFn(updateOutboundWebhook);
  const rotate = useServerFn(rotateWebhookSecret);
  const del = useServerFn(deleteOutboundWebhook);
  const test = useServerFn(sendTestWebhook);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({ queryKey: ["outbound_webhooks"], queryFn: () => fetchWh() });

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [editing, setEditing] = useState<any | null>(null);
  const [revealed, setRevealed] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: () =>
      create({
        data: {
          name: draft.name,
          url: draft.url,
          events: draft.events,
          description: draft.description || null,
          retry_limit: draft.retry_limit,
          timeout_ms: draft.timeout_ms,
        },
      }),
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ["outbound_webhooks"] });
      setRevealed(r.endpoint.signing_secret);
      setOpen(false);
      setDraft(EMPTY_DRAFT);
    },
    onError: (e: any) => toast.error(e?.message ?? "Create failed"),
  });

  const editMut = useMutation({
    mutationFn: () =>
      update({
        data: {
          id: editing.id,
          url: editing.url,
          events: editing.events ?? [],
          retry_limit: Number(editing.retry_limit) || 0,
          timeout_ms: Number(editing.timeout_ms) || 10000,
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["outbound_webhooks"] });
      setEditing(null);
      toast.success("Endpoint updated");
    },
    onError: (e: any) => toast.error(e?.message ?? "Update failed"),
  });

  const toggleActive = useMutation({
    mutationFn: (e: any) => update({ data: { id: e.id, active: !e.active } }),
    onSuccess: (_r, vars: any) => {
      qc.invalidateQueries({ queryKey: ["outbound_webhooks"] });
      toast.success(vars.active ? "Deliveries paused" : "Deliveries enabled");
    },
    onError: (e: any) => toast.error(e?.message ?? "Update failed"),
  });

  const rotateMut = useMutation({
    mutationFn: (id: string) => rotate({ data: { id } }),
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ["outbound_webhooks"] });
      setRevealed(r.signing_secret);
    },
    onError: (e: any) => toast.error(e?.message ?? "Rotate failed"),
  });

  const delMut = useMutation({
    mutationFn: (id: string) => del({ data: { id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["outbound_webhooks"] });
      toast.success("Endpoint deleted");
    },
  });

  const testMut = useMutation({
    mutationFn: (id: string) => test({ data: { endpoint_id: id, event: "test.ping" } }),
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ["webhook_deliveries"] });
      qc.invalidateQueries({ queryKey: ["outbound_webhooks"] });
      r?.ok ? toast.success(`Delivered (${r.response_status})`) : toast.error(`Failed${r?.error ? `: ${r.error}` : ""}`);
    },
    onError: (e: any) => toast.error(e?.message ?? "Test failed"),
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <CardTitle className="text-base">Outbound webhook endpoints</CardTitle>
            <CardDescription>Subscribe URLs to events. Payloads are signed with HMAC SHA-256.</CardDescription>
          </div>
          <Button onClick={() => { setDraft(EMPTY_DRAFT); setOpen(true); }}>
            <Plus className="h-4 w-4 mr-1" /> New endpoint
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>URL</TableHead>
              <TableHead>Events</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Last success</TableHead>
              <TableHead>Failures</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>}
            {!isLoading && (data?.endpoints ?? []).length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No webhook endpoints.</TableCell></TableRow>
            )}
            {(data?.endpoints ?? []).map((e: any) => (
              <TableRow key={e.id}>
                <TableCell className="font-medium">
                  {e.name}
                  {e.description && <div className="text-xs text-muted-foreground">{e.description}</div>}
                </TableCell>
                <TableCell className="font-mono text-xs max-w-[280px] truncate" title={e.url}>{e.url}</TableCell>
                <TableCell className="text-xs text-muted-foreground max-w-[220px]">
                  {(e.events ?? []).length === 0 ? (
                    <span className="text-amber-500">no events</span>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {(e.events as string[]).slice(0, 3).map((ev) => (
                        <Badge key={ev} variant="outline" className="text-[10px]">{ev}</Badge>
                      ))}
                      {e.events.length > 3 && <span>+{e.events.length - 3}</span>}
                    </div>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Switch checked={e.active} onCheckedChange={() => toggleActive.mutate(e)} />
                    <span className="text-xs text-muted-foreground">{e.active ? "Enabled" : "Paused"}</span>
                  </div>
                </TableCell>
                <TableCell className="text-xs whitespace-nowrap">{e.last_success_at ? new Date(e.last_success_at).toLocaleString() : "—"}</TableCell>
                <TableCell className="text-sm">{e.failure_count ?? 0}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => testMut.mutate(e.id)} disabled={testMut.isPending}>
                    <Send className="h-3 w-3 mr-1" /> Test
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditing({ ...e, events: e.events ?? [] })}>
                    Edit
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => rotateMut.mutate(e.id)}>
                    <RotateCcw className="h-3 w-3 mr-1" /> Rotate
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => {
                    if (confirm(`Delete webhook "${e.name}"?`)) delMut.mutate(e.id);
                  }}>
                    <Trash2 className="h-3 w-3 text-destructive" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New webhook endpoint</DialogTitle>
            <DialogDescription>The signing secret is shown once after creation. Store it safely.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label>Name</Label>
                <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Production billing hook" />
              </div>
              <div className="space-y-1">
                <Label>URL</Label>
                <Input value={draft.url} onChange={(e) => setDraft({ ...draft, url: e.target.value })} placeholder="https://example.com/hooks/coderlity" />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Description</Label>
              <Textarea rows={2} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} placeholder="What does this endpoint do?" />
            </div>
            <div className="space-y-1">
              <Label>Events</Label>
              <EventPicker selected={draft.events} onChange={(events) => setDraft({ ...draft, events })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Retry limit</Label>
                <Input type="number" min={0} max={20} value={draft.retry_limit} onChange={(e) => setDraft({ ...draft, retry_limit: Number(e.target.value) || 0 })} />
              </div>
              <div className="space-y-1">
                <Label>Timeout (ms)</Label>
                <Input type="number" min={500} max={60000} value={draft.timeout_ms} onChange={(e) => setDraft({ ...draft, timeout_ms: Number(e.target.value) || 10000 })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => createMut.mutate()} disabled={!draft.name || !draft.url || createMut.isPending}>
              {createMut.isPending ? "Creating…" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit endpoint</DialogTitle>
            <DialogDescription>{editing?.name}</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="space-y-1">
                <Label>URL</Label>
                <Input value={editing.url} onChange={(e) => setEditing({ ...editing, url: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>Events</Label>
                <EventPicker
                  selected={editing.events ?? []}
                  onChange={(events) => setEditing({ ...editing, events })}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Retry limit</Label>
                  <Input type="number" min={0} max={20} value={editing.retry_limit ?? 5} onChange={(e) => setEditing({ ...editing, retry_limit: Number(e.target.value) || 0 })} />
                </div>
                <div className="space-y-1">
                  <Label>Timeout (ms)</Label>
                  <Input type="number" min={500} max={60000} value={editing.timeout_ms ?? 10000} onChange={(e) => setEditing({ ...editing, timeout_ms: Number(e.target.value) || 10000 })} />
                </div>
              </div>
              <div className="rounded-md border border-border bg-muted/30 p-3 text-xs text-muted-foreground">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <div className="font-medium text-foreground">Signing secret</div>
                    For security, the secret value is not displayed. Use “Rotate” to generate a new one.
                  </div>
                  <Button size="sm" variant="outline" onClick={() => rotateMut.mutate(editing.id)}>
                    <RotateCcw className="h-3 w-3 mr-1" /> Rotate
                  </Button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={() => editMut.mutate()} disabled={editMut.isPending || !editing?.url}>
              {editMut.isPending ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Secret reveal dialog */}
      <Dialog open={!!revealed} onOpenChange={(o) => !o && setRevealed(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Copy your signing secret</DialogTitle>
            <DialogDescription>This is shown only once. Use it to verify HMAC signatures on incoming requests.</DialogDescription>
          </DialogHeader>
          <div className="rounded-md border bg-muted p-3 font-mono text-xs break-all">{revealed}</div>
          <DialogFooter>
            <Button variant="outline" onClick={() => revealed && copy(revealed)}><Copy className="h-4 w-4 mr-1" /> Copy</Button>
            <Button onClick={() => setRevealed(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

/* -------------------- DELIVERIES TAB -------------------- */
function DeliveriesTab() {
  const fetchD = useServerFn(listWebhookDeliveries);
  const retry = useServerFn(retryWebhookDelivery);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["webhook_deliveries"],
    queryFn: () => fetchD({ data: { limit: 100 } } as any),
    refetchInterval: 15_000,
  });

  const retryMut = useMutation({
    mutationFn: (id: string) => retry({ data: { id } }),
    onSuccess: (r: any) => {
      qc.invalidateQueries({ queryKey: ["webhook_deliveries"] });
      r?.ok ? toast.success(`Delivered (${r.response_status})`) : toast.error(`Failed${r?.error ? `: ${r.error}` : ""}`);
    },
    onError: (e: any) => toast.error(e?.message ?? "Retry failed"),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Delivery attempts</CardTitle>
        <CardDescription>Latest 100 outbound webhook attempts. Refreshes every 15s.</CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Time</TableHead>
              <TableHead>Event</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>HTTP</TableHead>
              <TableHead>Attempts</TableHead>
              <TableHead>Error</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>}
            {!isLoading && (data?.deliveries ?? []).length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No deliveries yet.</TableCell></TableRow>
            )}
            {(data?.deliveries ?? []).map((d: any) => (
              <TableRow key={d.id}>
                <TableCell className="text-xs whitespace-nowrap">{new Date(d.created_at).toLocaleString()}</TableCell>
                <TableCell className="font-mono text-xs">{d.event}</TableCell>
                <TableCell>{deliveryBadge(d.status)}</TableCell>
                <TableCell className="text-sm">{d.response_status ?? "—"}</TableCell>
                <TableCell className="text-sm">{d.attempts}/{d.max_attempts}</TableCell>
                <TableCell className="text-xs text-muted-foreground max-w-[280px] truncate">{d.error ?? "—"}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => retryMut.mutate(d.id)} disabled={retryMut.isPending}>
                    <RotateCcw className="h-3 w-3 mr-1" /> Retry
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

/* -------------------- SIGNING DOCS -------------------- */
function SigningDocs() {
  const sample = `// Node.js — verify an outbound webhook
import { createHmac, timingSafeEqual } from "crypto";

function verify(rawBody, headerSig, secret) {
  const provided = headerSig.replace(/^sha256=/, "");
  const expected = createHmac("sha256", secret).update(rawBody).digest("hex");
  const a = Buffer.from(provided, "hex");
  const b = Buffer.from(expected, "hex");
  return a.length === b.length && timingSafeEqual(a, b);
}`;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Signature verification</CardTitle>
        <CardDescription>
          Every outbound delivery includes <code className="font-mono">x-webhook-signature: sha256=&lt;hex&gt;</code>{" "}
          computed as <code className="font-mono">HMAC-SHA256(signing_secret, raw_body)</code>.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <pre className="rounded-md border bg-muted p-3 text-xs overflow-auto">{sample}</pre>
      </CardContent>
    </Card>
  );
}
